import React, { useState, useEffect } from 'react';

const AzureOfficeOnlineEditor = ({ 
  apiUrl = 'http://localhost:51373/api/Documents',
  currentUser = 'User1'
}) => {
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [viewMode, setViewMode] = useState('view'); // 'view' or 'edit'
  const [documentError, setDocumentError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');

  // Sample documents from Azure Blob Storage
  const [documentsList] = useState([
    {
      id: 1,
      name: 'Research_Key_Details.pdf',
      url: 'https://ascensionblob.blob.core.windows.net/teststorage/2b144f21-78cc-43b3-b0b8-7d4f928f2eb8/MyVaultDocument/E97B9711-1516-428C-84CB-2CC624CB9365/77f1ba/CRF Review, Approval & Green Light of New Studies.pdf$pdf',
      size: '245 KB',
      type: 'pdf',
      modified: '2024-08-12',
      icon: '📄'
    },
    {
      id: 2,
      name: 'Sales_Data.xlsx',
      url: 'https://ascensionblob.blob.core.windows.net/teststorage/fba496ac-0439-42f1-b9dc-f15a9821fece/MyVaultDocument/A6B47E3D-6010-4E59-8507-2C7E50D6341B/2109bc/Trial25Audit.xlsx',
      size: '156 KB',
      type: 'xlsx',
      modified: '2024-08-11',
      icon: '📊'
    },
    {
      id: 3,
      name: 'Marketing_Presentation.pptx',
      url: 'https://yourstorage.blob.core.windows.net/documents/Marketing_Presentation.pptx',
      size: '3.2 MB',
      type: 'pptx',
      modified: '2024-08-10',
      icon: '📈'
    },
    {
      id: 4,
      name: 'Contract.pdf',
      url: 'https://yourstorage.blob.core.windows.net/documents/Contract.pdf',
      size: '89 KB',
      type: 'pdf',
      modified: '2024-08-09',
      icon: '📋'
    },
    {
      id: 5,
      name: 'Notes.txt',
      url: 'https://yourstorage.blob.core.windows.net/documents/Notes.txt',
      size: '12 KB',
      type: 'txt',
      modified: '2024-08-08',
      icon: '📝'
    },
    {
      id: 6,
      name: 'Logo.png',
      url: 'https://yourstorage.blob.core.windows.net/documents/Logo.png',
      size: '234 KB',
      type: 'png',
      modified: '2024-08-07',
      icon: '🖼️'
    }
  ]);

  // Get Office Online URLs for different file types and modes
  const getOfficeOnlineUrl = (document, mode = 'view') => {
    const encodedUrl = encodeURIComponent(document.url);
    const fileType = document.type.toLowerCase();

    if (mode === 'edit') {
      // Edit URLs for Office Online
      switch(fileType) {
        case 'docx':
        case 'doc':
          return `https://word-edit.officeapps.live.com/we/wordeditorframe.aspx?src=${encodedUrl}&action=edit&wdPreviousSession=0`;
        case 'xlsx':
        case 'xls':
          return `https://excel.officeapps.live.com/x/_layouts/xlviewerinternal.aspx?src=${encodedUrl}&action=edit&wdPreviousSession=0`;
        case 'pptx':
        case 'ppt':
          return `https://powerpoint.officeapps.live.com/p/PowerPointFrame.aspx?src=${encodedUrl}&action=edit&wdPreviousSession=0`;
        default:
          return `https://view.officeapps.live.com/op/view.aspx?src=${encodedUrl}`;
      }
    } else {
      // View URLs for Office Online
      switch(fileType) {
        case 'docx':
        case 'doc':
        case 'xlsx':
        case 'xls':
        case 'pptx':
        case 'ppt':
          return `https://view.officeapps.live.com/op/view.aspx?src=${encodedUrl}`;
        case 'pdf':
          return `https://docs.google.com/viewer?url=${encodedUrl}&embedded=true`;
        default:
          return document.url; // Direct URL for images, etc.
      }
    }
  };

  // Check if file type can be edited
  const canEdit = (fileType) => {
    return ['docx', 'doc', 'xlsx', 'xls', 'pptx', 'ppt', 'txt'].includes(fileType.toLowerCase());
  };

  // Test document accessibility
  const testDocumentAccess = async (url) => {
    try {
      const response = await fetch(url, { method: 'HEAD' });
      return {
        accessible: response.ok,
        status: response.status,
        headers: Object.fromEntries([...response.headers.entries()])
      };
    } catch (error) {
      return {
        accessible: false,
        error: error.message
      };
    }
  };

  // Open document
  const openDocument = async (doc) => {
    setIsLoading(true);
    setDocumentError(null);
    setSaveStatus('');
    
    try {
      setSaveStatus('Testing document accessibility...');
      
      // Test if document is accessible
      const accessTest = await testDocumentAccess(doc.url);
      
      if (!accessTest.accessible) {
        setDocumentError(
          `Document not accessible (Status: ${accessTest.status || 'Network Error'}). ` +
          `Please check:\n` +
          `1. Azure Blob Storage permissions\n` +
          `2. Container access level (should be 'Blob' or 'Container')\n` +
          `3. Document URL: ${doc.url}`
        );
        setIsLoading(false);
        return;
      }

      setSelectedDocument(doc);
      setViewMode('view'); // Always start in view mode
      setSaveStatus('Document loaded successfully');
      
    } catch (error) {
      setDocumentError(`Error loading document: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Save document (for text files only)
  const saveTextDocument = async (content) => {
    if (!selectedDocument || selectedDocument.type !== 'txt') return;
    
    try {
      setSaveStatus('Saving...');
      
      const response = await fetch(`${apiUrl}/save-text`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fileName: selectedDocument.name,
          content: content,
          originalUrl: selectedDocument.url,
          editedBy: currentUser
        })
      });

      if (response.ok) {
        setSaveStatus('✅ Saved successfully!');
        setTimeout(() => setSaveStatus(''), 3000);
      } else {
        const error = await response.text();
        setSaveStatus(`❌ Save failed: ${error}`);
      }
    } catch (error) {
      setSaveStatus(`❌ Save error: ${error.message}`);
    }
  };

  // Render document content based on type
  const renderDocumentContent = () => {
    if (!selectedDocument) return null;

    const fileType = selectedDocument.type.toLowerCase();

    // Text file editor
    if (fileType === 'txt') {
      return <TextFileEditor 
        documentUrl={selectedDocument.url}
        viewMode={viewMode}
        onSave={saveTextDocument}
        saveStatus={saveStatus}
      />;
    }

    // Image display
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(fileType)) {
      return (
        <div style={{ 
          height: '600px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: '#f5f5f5'
        }}>
          <img 
            src={selectedDocument.url} 
            alt={selectedDocument.name}
            style={{ 
              maxWidth: '100%', 
              maxHeight: '100%',
              boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
            }} 
          />
        </div>
      );
    }

    // Office Online viewer/editor
    const officeUrl = getOfficeOnlineUrl(selectedDocument, viewMode);
    
    return (
      <iframe
        src={officeUrl}
        width="100%"
        height="600px"
        frameBorder="0"
        style={{ border: 'none' }}
        onLoad={() => {
          setSaveStatus(viewMode === 'edit' ? 
            '✏️ Editing mode - Changes auto-save to Azure' : 
            '👁️ View mode'
          );
        }}
        onError={() => {
          setDocumentError('Failed to load Office Online. Check document URL and permissions.');
        }}
      />
    );
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f0f2f5', fontFamily: 'Segoe UI, sans-serif' }}>
      
      {/* Header */}
      <div style={{ 
        backgroundColor: '#2c3e50', 
        color: 'white', 
        padding: '20px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <h1 style={{ margin: 0, fontSize: '24px' }}>
          🏢 Azure Documents - Office Online Editor
        </h1>
        <p style={{ margin: '5px 0 0 0', opacity: 0.8, fontSize: '14px' }}>
          View and edit documents directly from Azure Blob Storage
        </p>
      </div>

      <div style={{ display: 'flex', gap: '20px', padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
        
        {/* Document List Sidebar */}
        <div style={{ 
          width: '350px', 
          backgroundColor: 'white', 
          borderRadius: '12px', 
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          height: 'fit-content'
        }}>
          <div style={{ 
            padding: '20px', 
            borderBottom: '1px solid #e1e8ed',
            backgroundColor: '#f8f9fa',
            borderRadius: '12px 12px 0 0'
          }}>
            <h3 style={{ margin: '0 0 5px 0', color: '#2c3e50' }}>📁 Your Documents</h3>
            <p style={{ margin: 0, fontSize: '12px', color: '#7f8c8d' }}>
              {documentsList.length} files in Azure Storage
            </p>
          </div>
          
          <div style={{ padding: '10px' }}>
            {documentsList.map((doc) => (
              <div 
                key={doc.id}
                onClick={() => openDocument(doc)}
                style={{
                  padding: '15px',
                  margin: '5px',
                  border: selectedDocument?.id === doc.id ? '2px solid #3498db' : '1px solid #e1e8ed',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  backgroundColor: selectedDocument?.id === doc.id ? '#ebf3fd' : 'white',
                  transition: 'all 0.2s ease',
                  transform: selectedDocument?.id === doc.id ? 'scale(1.02)' : 'scale(1)'
                }}
                onMouseEnter={(e) => {
                  if (selectedDocument?.id !== doc.id) {
                    e.target.style.backgroundColor = '#f8f9fa';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedDocument?.id !== doc.id) {
                    e.target.style.backgroundColor = 'white';
                  }
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '24px' }}>{doc.icon}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ 
                      fontWeight: selectedDocument?.id === doc.id ? 'bold' : 'normal',
                      color: '#2c3e50',
                      fontSize: '14px',
                      marginBottom: '4px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {doc.name}
                    </div>
                    <div style={{ 
                      fontSize: '12px', 
                      color: '#7f8c8d',
                      display: 'flex',
                      justifyContent: 'space-between'
                    }}>
                      <span>{doc.size}</span>
                      <span>{doc.modified}</span>
                    </div>
                    <div style={{ 
                      fontSize: '10px', 
                      color: canEdit(doc.type) ? '#27ae60' : '#e74c3c',
                      marginTop: '2px'
                    }}>
                      {canEdit(doc.type) ? '✏️ Editable' : '👁️ View Only'}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Status Panel */}
          <div style={{ 
            margin: '10px',
            padding: '15px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            fontSize: '12px'
          }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#2c3e50' }}>📊 Status</h4>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
              <span>Documents:</span>
              <span style={{ fontWeight: 'bold' }}>{documentsList.length}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
              <span>Selected:</span>
              <span style={{ color: selectedDocument ? '#27ae60' : '#e74c3c' }}>
                {selectedDocument ? '✅' : '❌'}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Mode:</span>
              <span style={{ 
                color: viewMode === 'edit' ? '#e67e22' : '#3498db',
                fontWeight: 'bold'
              }}>
                {viewMode.toUpperCase()}
              </span>
            </div>
          </div>
        </div>

        {/* Document Editor Area */}
        <div style={{ 
          flex: 1, 
          backgroundColor: 'white', 
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          
          {selectedDocument ? (
            <>
              {/* Document Header */}
              <div style={{ 
                padding: '20px', 
                borderBottom: '1px solid #e1e8ed',
                backgroundColor: '#f8f9fa'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h2 style={{ margin: '0 0 5px 0', color: '#2c3e50', fontSize: '20px' }}>
                      {selectedDocument.icon} {selectedDocument.name}
                    </h2>
                    <div style={{ fontSize: '14px', color: '#7f8c8d' }}>
                      {saveStatus || `${selectedDocument.size} • Modified: ${selectedDocument.modified}`}
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      onClick={() => setViewMode('view')}
                      style={{
                        padding: '10px 16px',
                        backgroundColor: viewMode === 'view' ? '#3498db' : '#ecf0f1',
                        color: viewMode === 'view' ? 'white' : '#2c3e50',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        transition: 'all 0.2s'
                      }}
                    >
                      👁️ View
                    </button>
                    
                    {canEdit(selectedDocument.type) && (
                      <button
                        onClick={() => setViewMode('edit')}
                        style={{
                          padding: '10px 16px',
                          backgroundColor: viewMode === 'edit' ? '#e67e22' : '#ecf0f1',
                          color: viewMode === 'edit' ? 'white' : '#2c3e50',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          transition: 'all 0.2s'
                        }}
                      >
                        ✏️ Edit
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Error Display */}
              {documentError && (
                <div style={{ 
                  padding: '20px', 
                  backgroundColor: '#ffeaea', 
                  borderBottom: '1px solid #e74c3c',
                  color: '#c0392b'
                }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '10px' }}>❌ Document Error</div>
                  <pre style={{ 
                    margin: 0, 
                    fontSize: '12px', 
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'monospace'
                  }}>
                    {documentError}
                  </pre>
                </div>
              )}

              {/* Document Content */}
              {!documentError && !isLoading && (
                <div style={{ height: '600px' }}>
                  {renderDocumentContent()}
                </div>
              )}

              {/* Loading State */}
              {isLoading && (
                <div style={{ 
                  height: '600px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  color: '#7f8c8d'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    border: '4px solid #ecf0f1',
                    borderTop: '4px solid #3498db',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    marginBottom: '20px'
                  }}></div>
                  <p>Loading document...</p>
                </div>
              )}

            </>
          ) : (
            <div style={{ 
              height: '600px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              color: '#7f8c8d'
            }}>
              <div style={{ fontSize: '64px', marginBottom: '20px' }}>📄</div>
              <h3 style={{ color: '#2c3e50' }}>Select a document to get started</h3>
              <p>Choose any document from the sidebar to view or edit</p>
              <div style={{ 
                marginTop: '20px',
                fontSize: '14px',
                backgroundColor: '#f8f9fa',
                padding: '15px',
                borderRadius: '8px',
                maxWidth: '400px',
                textAlign: 'left'
              }}>
                <strong>Supported formats:</strong><br/>
                📄 Word documents (.docx, .doc)<br/>
                📊 Excel spreadsheets (.xlsx, .xls)<br/>
                📈 PowerPoint presentations (.pptx, .ppt)<br/>
                📋 PDF files (.pdf)<br/>
                📝 Text files (.txt)<br/>
                🖼️ Images (.png, .jpg, .gif)
              </div>
            </div>
          )}
        </div>
      </div>

      {/* CSS Animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

// Text File Editor Component
const TextFileEditor = ({ documentUrl, viewMode, onSave, saveStatus }) => {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [originalContent, setOriginalContent] = useState('');

  useEffect(() => {
    loadTextContent();
  }, [documentUrl]);

  const loadTextContent = async () => {
    try {
      const response = await fetch(documentUrl);
      const text = await response.text();
      setContent(text);
      setOriginalContent(text);
      setLoading(false);
    } catch (error) {
      console.error('Error loading text file:', error);
      setContent('Error loading file: ' + error.message);
      setLoading(false);
    }
  };

  const handleSave = () => {
    onSave(content);
  };

  const hasChanges = content !== originalContent;

  if (loading) {
    return (
      <div style={{ 
        height: '100%', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        Loading text file...
      </div>
    );
  }

  if (viewMode === 'view') {
    return (
      <div style={{ 
        height: '100%', 
        padding: '20px',
        backgroundColor: '#f8f9fa',
        fontFamily: 'Consolas, monospace',
        fontSize: '14px',
        overflow: 'auto',
        whiteSpace: 'pre-wrap'
      }}>
        {content}
      </div>
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ 
        padding: '10px 20px', 
        backgroundColor: '#f8f9fa', 
        borderBottom: '1px solid #e1e8ed',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <span style={{ fontSize: '12px', color: '#7f8c8d' }}>
          {saveStatus || (hasChanges ? '⚠️ Unsaved changes' : '✅ No changes')}
        </span>
        <button
          onClick={handleSave}
          disabled={!hasChanges}
          style={{
            padding: '8px 16px',
            backgroundColor: hasChanges ? '#27ae60' : '#bdc3c7',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: hasChanges ? 'pointer' : 'not-allowed',
            fontSize: '12px'
          }}
        >
          💾 Save Changes
        </button>
      </div>
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        style={{
          flex: 1,
          border: 'none',
          padding: '20px',
          fontSize: '14px',
          fontFamily: 'Consolas, monospace',
          resize: 'none',
          outline: 'none'
        }}
        placeholder="Edit your text file here..."
      />
    </div>
  );
};

export default AzureOfficeOnlineEditor;